import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import DependentEntitiesSelector, { commonPropTypes } from "./dependent-entities-selector";
import DataGrid, { DataGridItem } from "components/data-grid";
import { getScoreboardTemplateThumbnail } from "config/paths";
import { useAuthDataContext } from "components/auth";
import { injectIntl } from "react-intl";
import { scoreboardTemplateStorage } from "pages/event/forms/utils";
import styles from "./styles/scoreboard-template-select.css.js";
import withStyles from "components/with-styles";

const { fetchScoreboardTemplate } = scoreboardTemplateStorage;

const query = `
  query ScoreboardTemplates($contractId: ID, $eventTypeId: ID) {
    scoreboardTemplates(contractId: $contractId, eventTypeId: $eventTypeId) {
      items {
        id
        codename
        isDefault
      }
      total
    }
  }
`;

const ScoreboardTemplateSelect = ({
  multiple = false,
  id = "scoreboardTemplateId",
  name = "scoreboardTemplateId",
  contractId,
  eventTypeId,
  isInsideForm,
  onChange,
  intl,
  value,
  isNewForm = false,
  classes,
  onClick,
  ...props
}) => {
  const lastSelectedScoreboardTemplateId = fetchScoreboardTemplate();
  const { token } = useAuthDataContext();
  const handleDataFromResponse = useCallback(
    data => (data && data.scoreboardTemplates ? data.scoreboardTemplates : { items: [], total: 0 }),
    [],
  );

  const handleGetValue = useCallback(element => element.id, []);
  const handleGetLabel = useCallback(
    element => {
      return (
        <DataGrid>
          <DataGridItem>
            <img
              onClick={e => {
                e.preventDefault();
              }}
              className={classes.preview_image}
              src={getScoreboardTemplateThumbnail({ contractId, scoreboardTemplateId: element.id, token })}
            />
          </DataGridItem>
          <DataGridItem>{element.codename}</DataGridItem>
        </DataGrid>
      );
    },
    [classes.preview_image, contractId, token],
  );

  const [dependencyContractId, setDependencyContractId] = useState(contractId);
  const [dependencyEventTypeId, setDependencyEventTypeId] = useState(eventTypeId);

  useEffect(
    () => {
      if (contractId !== dependencyContractId) {
        setDependencyContractId(contractId);
        onChange({ scoreboardTemplateId: null });
      }
    },
    [contractId, dependencyContractId, onChange],
  );

  useEffect(
    () => {
      if (eventTypeId !== dependencyEventTypeId) {
        setDependencyEventTypeId(eventTypeId);
        onChange({ scoreboardTemplateId: null });
      }
    },
    [eventTypeId, dependencyEventTypeId, onChange],
  );

  return (
    <DependentEntitiesSelector
      selectClasses={{ disabled: classes.selector__clickable }}
      onClick={onClick}
      getDefaultItemSelected={
        !isNewForm
          ? null
          : elements =>
              elements.items.find((item, i) => item.id === lastSelectedScoreboardTemplateId) ||
              elements.items.find((item, i) => item.isDefault)
      }
      disabled={!contractId || !eventTypeId}
      queryParams={{
        contractId,
        eventTypeId,
      }}
      emptyValue={""}
      emptyValueText={intl.formatMessage({ id: "common.none" })}
      skip={!contractId || !eventTypeId}
      isInsideForm={isInsideForm}
      multiple={multiple}
      id={id}
      name={name}
      queryString={query}
      onGetDataFromResponse={handleDataFromResponse}
      onGetValue={handleGetValue}
      onGetLabel={handleGetLabel}
      onChange={onChange}
      value={value}
      skipOnFirstRender={isNewForm}
      {...props}
    />
  );
};

ScoreboardTemplateSelect.propTypes = {
  ...commonPropTypes,
  /**
   * Valor del selector
   */
  selectedElement: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Valor del selector
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Contrato para el que se mostrarán los templates
   */
  contractId: PropTypes.string,
  /**
   * Callback que se llama al hacer clic sobre el selector
   */
  onClick: PropTypes.func,
};

export default injectIntl(withStyles(styles)(ScoreboardTemplateSelect));
